<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcFixedAssetGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    fixedAsset: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_n2g4rgv1n_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_ca6vxrxer_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_ok8wf0xep_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MFixedAssetEditor',
      props: {
        target: 'general',
        fixedAsset: _.cloneDeep(props.fixedAsset),
      },
    };
  };
  const act_8352907821006950_emit_4bfhxknya_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_7315092382398562_reloadComponentData_yw3axgsou =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_nnmfj8uid =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_yw3axgsou,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_nnmfj8uid],
    event: 'reload',
  });
  const act_2095089031469157_emit_n2g4rgv1n = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_n2g4rgv1n_getActionArgs,
  });
  const inst_evh_2095089031469157_close_ca6vxrxer =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_n2g4rgv1n,
      event: 'close',
      when: inst_evh_2095089031469157_close_ca6vxrxer_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_ok8wf0xep =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_ok8wf0xep_getActionArgs,
      events: [inst_evh_2095089031469157_close_ca6vxrxer],
    });
  const inst_evh_2095089031469157_clickPreventStop_5epsbby5v =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_ok8wf0xep,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_5epsbby5v],
      event: 'click.prevent.stop',
    });
  const act_8352907821006950_emit_4bfhxknya = new actionTypes.EmitAction({
    actionArgs: act_8352907821006950_emit_4bfhxknya_getActionArgs,
  });
  const inst_evh_8352907821006950_reload_3cxtyjvma =
    new actionTypes.EventHandlerImpl({
      action: act_8352907821006950_emit_4bfhxknya,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_8352907821006950_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8352907821006950_reload_3cxtyjvma],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_nnmfj8uid,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_ca6vxrxer,
    inst_evh_2095089031469157_clickPreventStop_5epsbby5v,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_8352907821006950_reload_3cxtyjvma,
    inst_evh_8352907821006950_reload,
  };

  const evh_7315092382398562_reload_nnmfj8uid = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_nnmfj8uid.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_ca6vxrxer = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_ca6vxrxer.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_5epsbby5v = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_5epsbby5v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8352907821006950_reload_3cxtyjvma = async (event, scope) => {
    return await actions.inst_evh_8352907821006950_reload_3cxtyjvma.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8352907821006950_reload = async (event, scope) => {
    return await actions.inst_evh_8352907821006950_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('FixedAsset', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem :label="pgettext('FixedAsset', 'Asset Type')">
          <UcFixedAssetTypeBadge :value="fixedAsset.asset_type">
          </UcFixedAssetTypeBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Name')"
          :content="fixedAsset.name"
          v-if="fixedAsset.name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Description')"
          :content="fixedAsset.description"
          v-if="fixedAsset.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Inventory Number')"
          :content="fixedAsset.inventory_no"
          v-if="fixedAsset.inventory_no"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('FixedAsset', 'Rental')">
          <UcFixedAssetRentalCard
            :fixedAsset="fixedAsset"
            @reload="evh_8352907821006950_reload($event, {})"
          >
          </UcFixedAssetRentalCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'SKU')"
          :content="fixedAsset.sku"
          v-if="fixedAsset.sku"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Type Number')"
          :content="fixedAsset.type_no"
          v-if="fixedAsset.type_no"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Quantity')"
          :content="number(fixedAsset.quantity)"
          v-if="fixedAsset.quantity"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Cost')"
          :content="number(fixedAsset.cost)"
          v-if="fixedAsset.cost"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'General Ledger Code')"
          :content="fixedAsset.gl_code"
          v-if="fixedAsset.gl_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Year of Production')"
          :content="fixedAsset.year_of_production"
          v-if="fixedAsset.year_of_production"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Manufacturer')"
          :content="fixedAsset.manufacturer"
          v-if="fixedAsset.manufacturer"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Serial Number')"
          :content="fixedAsset.serial_number"
          v-if="fixedAsset.serial_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Category')"
          :content="fixedAsset.category"
          v-if="fixedAsset.category"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('FixedAsset', 'Groups')"
          :content="fixedAsset.groups"
          v-if="fixedAsset.groups"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
